import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Modal from "@mui/material/Modal";
import {
  TextField,
  IconButton,
  Collapse,
  Switch,
  MenuItem,
  Select,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { storeOrderChange } from "../../../../../services/orders.services";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import CircularLoader from "../../../../../Components/CircularLoader/CircularLoader";
import usePickedFiles from "../../../../../hooks/usePickedFiles";
import {
  appendFiles,
  uploadLabel,
  uploadProgress,
} from "../../../../../helpers/uploads";
import noImage from "../../../../../assets/images/noImage.png";
import { productNote } from "../../../../Shipper/Customer/MainPages/Orders/Components/DeliveryRoutes/lineNoteText";
import Checkbox from "@mui/material/Checkbox";
import { Trans, useTranslation } from "react-i18next";
import { MdPhotoCamera } from "react-icons/md";
import useMediaQuery from "@mui/material/useMediaQuery";
import StatusLinesPhone, { saleableOptions } from "./StatusLinesPhone";

// Phones get one compact row per product (StatusLinesPhone); from md up, the
// table.
const PHONE_QUERY = "(max-width: 767.98px)";
const CELL = "border-[1px] border-ui-border p-1 text-sm";
const PALLET_ROW =
  "flex flex-nowrap items-center justify-between border-b py-[4px] text-sm";

export default function ConformModal({
  open,
  setOpen,
  setSnack,
  setOrders,
  selectedStatus,
  data,
  setData,
  orderStatus,
  setselectedStatus,
  // (optional) Line values to start from, by line id — the order view's
  // Fulfil bar passes what was ticked as picked: { is_saleable,
  // saleable_item_quantity }.
  prefill = null,
}) {
  const { t } = useTranslation(["warehouse", "common"]);
  // Photos are shrunk as soon as they're picked (see helpers/uploads).
  const picked = usePickedFiles();
  const [upload, setUpload] = useState({ preparing: false, percent: null });
  const [notes, setnotes] = useState();
  const [StatusName, setStatusName] = useState();
  const [loader, setloader] = useState(false);
  const [orderDetails, setorderDetails] = useState([]);

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const phone = useMediaQuery(PHONE_QUERY, { noSsr: true });

  // Phones: a line's fulfilment note field shows under its row once Note is
  // tapped (or when it has a note); Note again closes an empty one.
  const [openNotes, setOpenNotes] = useState({});
  const noteFields = useRef({});
  const toggleNote = (index) => {
    if (openNotes[index] && !orderDetails[index]?.fulfillment_note) {
      setOpenNotes((prev) => ({ ...prev, [index]: false }));
      return;
    }
    flushSync(() => setOpenNotes((prev) => ({ ...prev, [index]: true })));
    noteFields.current[index]?.focus();
  };

  const handleClose = () => {
    setStatusName();
    setnotes();
    picked.clear();
    setUpload({ preparing: false, percent: null });
    setorderDetails([]);
    setOpenNotes({});
    setOpen(false);
  };

  const handleCancel = () => {
    setselectedStatus(data?.fullfilment_status?.id);
    handleClose();
  };

  useEffect(() => {
    setStatusName(
      orderStatus
        .find((order) => order.id === selectedStatus)
        ?.name?.toUpperCase()
    );
  }, [selectedStatus]);

  const handleFileChange = (e) => {
    picked.add(e.target.files);
    // The same photo can be picked again after it's removed.
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    picked.remove(index);
  };

  const handleRenameFile = (index, newName) => {
    picked.rename(index, newName);
  };

  const handleSave = async () => {
    setloader(true);
    let response;
    try {
      // Photos still being shrunk are waited for ("Preparing photos…").
      setUpload({ preparing: picked.preparing, percent: null });
      const files = await picked.ready();
      setUpload({ preparing: false, percent: null });
      const formData = new FormData();
      appendFiles(formData, files);
      if (selectedStatus === 5) { // Assuming 5 is the ID for "Attention"
        // The note typed here is the fulfilment note; the product's own
        // note (the shipper's, from the order) isn't sent, so it never
        // changes.
        const modifiedOrderDetails = orderDetails.map(
          ({ is_saleable, saleable_item_quantity, id, fulfillment_note }) => ({
            is_saleable,
            saleable_item_quantity,
            fulfillment_note,
            id,
          })
        );
        formData.append(`order_details`, JSON.stringify(modifiedOrderDetails));
      }
      if (StatusName === "FULFILLED") {
        const modifiedOrderDetails = orderDetails.map(
          ({ pallet_data, id }) => ({
            pallet_data,
            id,
          })
        );
        formData.append(`order_details`, JSON.stringify(modifiedOrderDetails));
      }

      if (data?.amount_received_by_warehouse) {
        formData.append(
          `amount_received_by_warehouse`,
          data?.amount_received_by_warehouse ?? ""
        );
      }
      formData.append(`note`, notes ?? "");
      formData.append(`order_id`, data?.id);
      formData.append(`status`, data?.status);
      formData.append(`fulfillment_order_status`, StatusName);
      // With files, the button shows how far the upload is.
      const progress = files.length
        ? [
            {
              onUploadProgress: uploadProgress((percent) =>
                setUpload({ preparing: false, percent })
              ),
            },
          ]
        : [];
      response = await storeOrderChange(
        formData,
        data?.id,
        selectedStatus,
        ...progress
      );
      setUpload({ preparing: false, percent: null });
      if (!response.error) {
        setloader(false);
        setOrders((prevOrder) => {
          return {
            ...prevOrder,
            data: prevOrder?.data.map((order) =>
              order.id === data.id
                ? {
                    ...order,
                    order_notes: response?.data?.result[0]?.order_notes,
                    fullfilment_status: {
                      id: selectedStatus,
                      name: StatusName,
                    },
                    order_details: response?.data?.result[0]?.order_details,
                  }
                : order
            ),
          };
        });
        setData({
          ...data,
          order_notes: response?.data?.result[0]?.order_notes,
          fullfilment_status: {
            id: selectedStatus,
            name: StatusName,
          },
          order_details: response?.data?.result[0]?.order_details,
        });
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: response?.data?.message,
            severity: "success",
          };
        });
        handleClose();
      } else {
        setloader(false);
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: t("orderDetails.somethingWentWrongTryAgainLater"),
            severity: "error",
          };
        });
      }
    } catch (error) {
      setloader(false);
      setUpload({ preparing: false, percent: null });
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text:
            error?.response?.data?.message ??
            t("orderDetails.somethingWentWrongTryAgainLater"),
          severity: "error",
        };
      });
    }
  };

  const handleStatusChange = async (e, index) => {
    const orderList = [...orderDetails];
    orderList[index]["is_saleable"] = e.target.checked;
    orderList[index]["saleable_item_quantity"] =
      e.target.checked === true ? orderList[index]["item_quantity"] : "0";
    setorderDetails(orderList);
  };

  const handleSaleableSelect = async (e, index) => {
    const orderList = [...orderDetails];
    orderList[index]["saleable_item_quantity"] = e.target.value;
    setorderDetails(orderList);
  };

  const handleAttentionNote = async (e, index) => {
    const orderList = [...orderDetails];
    // A copy of the line: the order view's own copy keeps showing what was
    // saved until this is.
    orderList[index] = {
      ...orderList[index],
      fulfillment_note: e.target.value,
    };
    setorderDetails(orderList);
  };

  const handleAmount = async (value) => {
    setData({
      ...data,
      amount_received_by_warehouse: value,
    });
  };

  const handleLocationSelect = (selectedPalletData, index) => {
    setorderDetails((prevItems) => {
      const updatedItems = [...prevItems];
      updatedItems[index] = {
        ...updatedItems[index],
        pallet_data: selectedPalletData,
      };
      return updatedItems;
    });
  };

  const handlePalletSelect = (selectedPallet, index) => {
    const exists = orderDetails?.[index]?.pallet_data?.some(
      (data) => data?.pallet_id == selectedPallet
    );

    // Prepare updatedPalletData
    let updatedPalletData;

    if (exists) {
      // Remove the object that has the matching pallet_id
      updatedPalletData = orderDetails?.[index]?.pallet_data?.filter(
        (data) => data?.pallet_id != selectedPallet
      );
    } else {
      // Add a new object if the selectedPallet doesn't exist
      updatedPalletData = [
        ...(orderDetails?.[index]?.pallet_data ?? []),
        { pallet_id: parseInt(selectedPallet), quantity: 0 },
      ];
    }

    // Update the state
    setorderDetails((prevItems) => {
      const updatedItems = [...prevItems];
      updatedItems[index] = {
        ...updatedItems[index],
        pallet_data: updatedPalletData,
      };
      return updatedItems;
    });
  };

  useEffect(() => {
    if (!data?.order_details?.length || !open) return;

    const boltItems = data.order_details.filter(
        (item) => !item.plugin_item_id
      );

    const updatedOrderDetails = boltItems.map((line) => {
      const seed = prefill?.[String(line?.id)];
      const order = seed ? { ...line, ...seed } : line;
      const pallets = order?.products?.product_pallets;

      if (!Array.isArray(pallets) || pallets.length === 0) return order;

      const validPallets = pallets.filter(
        (p) => p.quantity >= Number(order?.item_quantity ?? 0)
      );
      if (validPallets.length === 0) return order;

      const lowestPallet = validPallets.reduce((min, curr) =>
        curr.quantity < min.quantity ? curr : min
      );

      return {
        ...order,
        pallet_data: [
          {
            pallet_id: Number(lowestPallet.pallet_id),
            quantity: Number(order?.item_quantity ?? 0),
          },
        ],
      };
    });
    setorderDetails(updatedOrderDetails);
  }, [data?.order_details, open, prefill]);

  const showProductTable =
    (StatusName === "FULFILLED" || selectedStatus === 5) &&
    Boolean(orderDetails?.length);

  return (
    <div>
      <Modal
        open={open}
        // onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
        className="inner__detail_fulfilment_modal"
      >
        <div
          className={`absolute left-[0px] top-[0px] flex h-full w-full flex-col overflow-hidden bg-ui-surface shadow-ui-md focus:outline-none md:left-1/2 md:top-1/2 md:h-auto md:max-h-[90vh] md:w-[92vw] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg ${
            showProductTable ? "md:max-w-[1200px]" : "md:max-w-[520px]"
          }`}
        >
          {/* Phones: a full-screen sheet with Decline / Confirm always at
              the bottom. From md up: the centred window. */}
          <h2
            id="child-modal-title"
            className="m-0 shrink-0 border-b border-ui-border px-[12px] py-[11px] text-[15px] font-semibold text-ui-text md:px-[24px] md:py-[16px]"
          >
            {t("orderDetails.saleStatusUpdate")}
          </h2>
          <div className="thin__scrollbar min-h-0 flex-1 overflow-y-auto px-[12px] py-[12px] md:px-[24px] md:py-[16px]">

          {showProductTable && phone ? (
            <div className="mb-[12px]">
              <StatusLinesPhone
                lines={orderDetails}
                attention={selectedStatus === 5} // Assuming 5 is the ID for "Attention"
                fulfilled={StatusName === "FULFILLED"}
                onSaleable={handleStatusChange}
                onSaleableQty={handleSaleableSelect}
                onNote={handleAttentionNote}
                openNotes={openNotes}
                onOpenNote={toggleNote}
                noteFields={noteFields}
                expanded={expandedRows}
                onToggle={toggleRow}
                onPalletToggle={handlePalletSelect}
                onPalletQty={handleLocationSelect}
              />
            </div>
          ) : null}
          {showProductTable && !phone ? (
            <div className="invmodal__shipment_details sales_update_inner_modal">
              <div
                className="invmodal__shipment_summaryhding"
                style={{ marginTop: "5px" }}
              >
                {t("orderDetails.productDetails")}
              </div>
              <div className="invmodal__shdetails_content sales_update_inner_modal_table">
                <div className="invmodal__shdetails_cntleft">
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto border-[1px] border-ui-border">
                      <thead className="bg-[var(--color-ui-border)]">
                        <tr>
                          <th className="p-1 text-center text-sm border border-ui-border"></th>
                          <th className="p-1 text-center text-sm border border-ui-border">
                            #
                          </th>
                          <th className="p-1 text-left text-sm border border-ui-border">
                            {t("orderDetails.name")}
                          </th>
                          <th className="p-1 text-center text-sm border border-ui-border">
                            {t("orderDetails.sku")}
                          </th>
                          <th className="p-1 text-center text-sm border border-ui-border">
                            {t("orderDetails.qty")}
                          </th>
                          <th className="p-1 text-left text-sm border border-ui-border">
                            {t("orderDetails.lotBatch")}
                          </th>
                          {selectedStatus === 5 && ( // Assuming 5 is the ID for "Attention"
                            <>
                              <th className="p-1 text-left text-sm border border-ui-border">
                                {t("orderDetails.saleable")}
                              </th>
                              <th className="p-1 text-center text-sm border border-ui-border">
                                {t("orderDetails.saleableQty")}
                              </th>
                              <th className="p-1 text-left text-sm border border-ui-border">
                                {t("orderDetails.fulfilmentNote")}
                              </th>
                            </>
                          )}
                          {StatusName === "FULFILLED" && (
                            <th className="p-1 text-left text-sm border border-ui-border">
                              {t("orderDetails.selectedPallets")}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetails?.map((item, index) => (
                          <React.Fragment key={index}>
                            <tr className="odd:bg-ui-surface even:bg-ui-surface-sunken">
                              <td className={`${CELL} text-center`}>
                                {StatusName === "FULFILLED" && (
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRow(index)}
                                    aria-expanded={expandedRows[index] || false}
                                    aria-label={t("orderDetails.expandRow")}
                                    className="!p-2 lg:!p-[5px] [@media(pointer:coarse)]:!p-2"
                                  >
                                    {expandedRows[index] ? (
                                      <KeyboardArrowUp />
                                    ) : (
                                      <KeyboardArrowDown />
                                    )}
                                  </IconButton>
                                )}
                              </td>
                              <td className={`${CELL} text-center`}>
                                <img
                                  src={
                                    item?.products?.media?.[0]?.file || noImage
                                  }
                                  className="mx-auto h-9 w-9 object-cover"
                                  alt={t("orderDetails.product")}
                                  onError={(e) => {
                                    e.target.src = noImage;
                                  }}
                                />
                              </td>
                              <td className={`${CELL} min-w-0 text-left`}>
                                {item?.products?.name ?? "-"}
                                {/* The shipper's note on this product: shown
                                    to whoever fulfils it, never edited here. */}
                                {productNote(item) && (
                                  <span className="mt-[3px] block rounded-[4px] bg-state-warning-bg px-[6px] py-[2px] text-[12px] font-normal leading-[1.4] text-state-warning-text">
                                    <b>{t("orderDetails.productNoteColon")}</b> {productNote(item)}
                                  </span>
                                )}
                              </td>
                              <td className={`${CELL} min-w-0 break-words text-center`}>
                                {item?.products?.code ?? "-"}
                              </td>
                              <td className={`${CELL} whitespace-nowrap text-center`}>
                                {item?.item_quantity ?? "-"}
                              </td>
                              <td className={`${CELL} text-center`}>
                                {item?.order_item_product_lot_quantities?.map(
                                  (product_lot, index) => (
                                    <React.Fragment key={index}>
                                      {product_lot?.product_lot?.name} (
                                      {product_lot?.quantity})
                                      <br />
                                    </React.Fragment>
                                  )
                                )}
                              </td>
                              {StatusName === "FULFILLED" && (
                                <td className={`${CELL} min-w-0 max-w-[250px] text-left`}>
                                  {item?.pallet_data?.length > 0 ? (
                                    item?.pallet_data.map((pallet, idx) => {
                                      const palletDetails =
                                        item?.products?.product_pallets?.find(
                                          (product) =>
                                            product.pallet.id ===
                                            pallet.pallet_id
                                        );
                                      return (
                                        <div
                                          key={idx}
                                          className="flex flex-wrap gap-x-2 text-sm"
                                          onClick={() => toggleRow(index)}
                                          style={{ cursor: "pointer" }}
                                        >
                                          <span className="font-medium">
                                            {palletDetails?.pallet?.name ??
                                              "N/A"}
                                          </span>
                                          <span className="text-ui-text-muted">
                                            {palletDetails?.pallet?.location
                                              ?.name
                                              ? `(${palletDetails.pallet.location.name})`
                                              : ""}
                                          </span>
                                          <span className="min-w-[40px]">
                                            {t("orderDetails.qtyColon")} {pallet.quantity}
                                          </span>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <span
                                      className="cursor-pointer text-ui-text-subtle"
                                      onClick={() => toggleRow(index)}
                                    >
                                      {t("orderDetails.noPalletsSelected")}
                                    </span>
                                  )}
                                </td>
                              )}
                              {selectedStatus === 5 && ( // Assuming 5 is the ID for "Attention"
                                <>
                                  <td className={`${CELL} text-left`}>
                                    <Switch
                                      checked={item?.is_saleable || false}
                                      onChange={(e) =>
                                        handleStatusChange(e, index)
                                      }
                                      color="primary"
                                    />
                                  </td>
                                  <td className={`${CELL} text-center`}>
                                    <Select
                                      value={
                                        item?.saleable_item_quantity || "0"
                                      }
                                      onChange={(e) =>
                                        handleSaleableSelect(e, index)
                                      }
                                      className="w-full"
                                      size="small"
                                    >
                                      {saleableOptions(item).map((quantity) => (
                                        <MenuItem key={quantity} value={quantity}>
                                          {quantity}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </td>
                                  <td className={`${CELL} text-left`}>
                                    <TextField
                                      placeholder={t("orderDetails.fulfilmentNote2")}
                                      value={item?.fulfillment_note ?? ""}
                                      onChange={(e) =>
                                        handleAttentionNote(e, index)
                                      }
                                      multiline
                                      size="small"
                                      fullWidth
                                    />
                                  </td>
                                </>
                              )}
                            </tr>

                            {StatusName === "FULFILLED" && (
                              <tr>
                                <td colSpan="100%" className="border-t p-0">
                                  <Collapse
                                    in={expandedRows[index]}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <div className="bg-ui-surface-sunken p-3">
                                      <h4 className="mb-2 text-sm font-medium text-ui-text">
                                        {t("orderDetails.productPallets")}
                                      </h4>
                                      <div className="flex justify-between py-1 font-semibold border-b text-sm">
                                        <span className="flex-1 text-start">
                                          {t("orderDetails.selection")}
                                        </span>
                                        <span className="flex-1 text-start">
                                          {t("orderDetails.code")}
                                        </span>
                                        <span className="flex-1 text-start">
                                          {t("orderDetails.palletName")}
                                        </span>
                                        <span className="flex-1 text-start">
                                          {t("orderDetails.lotBatch")}
                                        </span>
                                        <span className="flex-1 text-start">
                                          {t("orderDetails.location")}
                                        </span>
                                        <span className="flex-1 text-start">
                                          {t("orderDetails.qty")}
                                        </span>
                                      </div>
                                      {item?.products?.product_pallets?.map(
                                        (pallet, idx) => (
                                          <div key={idx} className={PALLET_ROW}>
                                            <span className="flex-1 text-start">
                                              <Checkbox
                                                checked={
                                                  item?.pallet_data?.some(
                                                    (data) =>
                                                      data?.pallet_id ==
                                                      pallet?.pallet_id
                                                  ) || false
                                                }
                                                onChange={() => {
                                                  handlePalletSelect(
                                                    pallet?.pallet_id,
                                                    index
                                                  );
                                                }}
                                                inputProps={{
                                                  "aria-label": "controlled",
                                                }}
                                              />
                                            </span>
                                            <span className="flex-1 text-start">
                                              {pallet?.pallet?.code ?? "N/A"}
                                            </span>
                                            <span className="flex-1 text-start">
                                              {pallet?.pallet?.name ?? "N/A"}
                                            </span>
                                            <span className="flex-1 text-start">
                                              {pallet?.product_lot?.name ??
                                                "N/A"}
                                            </span>
                                            <span className="flex-1 text-start">
                                              {pallet?.pallet?.location?.name ??
                                                "N/A"}
                                            </span>
                                            <span className="flex-1 text-start">
                                              {pallet?.quantity ?? "N/A"}
                                            </span>
                                          </div>
                                        )
                                      )}

                                      <h4 className="mb-2 mt-[16px] text-sm font-bold text-ui-text">
                                        {t("orderDetails.selectedPallets")}
                                      </h4>
                                      <div className="flex justify-between py-1 font-semibold border-b text-sm">
                                        <span className="flex-1 text-start">
                                          {t("orderDetails.code")}
                                        </span>
                                        <span className="flex-1 text-start">
                                          {t("orderDetails.palletName")}
                                        </span>
                                        <span className="flex-1 text-center">
                                          {t("orderDetails.lotBatch")}
                                        </span>
                                        <span className="flex-1 text-center">
                                          {t("orderDetails.location")}
                                        </span>
                                        <span className="flex-1 text-center">
                                          {t("orderDetails.qty")}
                                        </span>
                                      </div>
                                      {item?.pallet_data?.length > 0 ? (
                                        <>
                                          {item?.pallet_data?.map(
                                            (data, idx) => {
                                              // Find the product pallet for this data
                                              const productPallet =
                                                item?.products?.product_pallets?.find(
                                                  (product) =>
                                                    product.pallet.id ===
                                                    data.pallet_id
                                                );

                                              // Default to "N/A" if productPallet is not found
                                              const palletCode =
                                                productPallet?.pallet?.code ||
                                                "N/A";
                                              const palletName =
                                                productPallet?.pallet?.name ||
                                                "N/A";
                                              const productLotName =
                                                productPallet?.product_lot
                                                  ?.name || "N/A";
                                              const palletLocationName =
                                                productPallet?.pallet?.location
                                                  ?.name || "N/A";

                                              return (
                                                <div key={idx} className={PALLET_ROW}>
                                                  <span className="flex-1 text-start">
                                                    {palletCode}
                                                  </span>
                                                  <span className="flex-1 text-start">
                                                    {palletName}
                                                  </span>
                                                  <span className="flex-1 text-center">
                                                    {productLotName}
                                                  </span>
                                                  <span className="flex-1 text-center">
                                                    {palletLocationName}
                                                  </span>
                                                  <span className="flex-1 text-center">
                                                    <TextField
                                                      type="number"
                                                      value={data.quantity}
                                                      onChange={(e) => {
                                                        const updatedPalletData =
                                                          [...item.pallet_data];
                                                        updatedPalletData[
                                                          idx
                                                        ].quantity =
                                                          e.target.value;
                                                        handleLocationSelect(
                                                          updatedPalletData,
                                                          index
                                                        );
                                                      }}
                                                      size="small"
                                                      error={
                                                        parseInt(
                                                          data.quantity
                                                        ) >
                                                        parseInt(
                                                          productPallet?.quantity
                                                        )
                                                      }
                                                    />
                                                  </span>
                                                </div>
                                              );
                                            }
                                          )}
                                        </>
                                      ) : (
                                        <span className="flex justify-center text-sm text-ui-text-subtle">
                                          {t("orderDetails.noPalletsSelected2")}
                                        </span>
                                      )}
                                    </div>
                                  </Collapse>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {(StatusName === "FULFILLED" ||
          StatusName === "CANCELLED" ||
          selectedStatus === 5 // Assuming 5 is the ID for "Attention"
            ? true
            : false) && (
            <>
              <TextField
                label={t("orderDetails.note")}
                type="text"
                name="Note"
                value={notes}
                className="sale__status_notes"
                onChange={(e) => setnotes(e.target.value)}
                placeholder={"Note for Sale " + StatusName}
                sx={{
                  width: "100%",
                  input: { font: "menu" },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                multiline={true}
                rows={2}
              />
              {(data?.payment_method_title === "Cash On Delivery" ||
                data?.payment_method_title === "Cash On Pickup") &&
                StatusName === "FULFILLED" && (
                  <div style={{ marginBottom: "5px" }}>
                    <TextField
                      label={t("orderDetails.pendingAmount")}
                      type="number"
                      name="amount_pending"
                      value={data?.amount_pending ?? ""}
                      className="sale__status_notes"
                      placeholder={t("orderDetails.amountToBeReceived")}
                      sx={{
                        width: "50%",
                        input: { font: "menu" },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      disabled
                    />
                    <TextField
                      label={t("orderDetails.receivedAmount")}
                      type="number"
                      name="amount_received_by_warehouse"
                      value={data?.amount_received_by_warehouse ?? ""}
                      className="sale__status_notes"
                      onChange={(e) => handleAmount(e.target.value)}
                      placeholder={t("orderDetails.enterReceivedAmount")}
                      sx={{
                        width: "50%",
                        input: { font: "menu" },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      error={
                        data?.amount_pending !=
                        data?.amount_received_by_warehouse
                          ? true
                          : false
                      }
                    />
                  </div>
                )}
              <div className="sale_status_update_attachment_wrap">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="file-input"
                />
                {/* Phones and tablets: straight to the camera, one photo
                    after another (Attach File still picks from the
                    library or files). */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="photo-input"
                />
                {/* Side by side (Take photo only on touch screens). */}
                <div className="flex gap-2">
                  <label
                    htmlFor="photo-input"
                    className="inline-flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border-[1px] border-brand bg-brand-light px-3 py-1.5 text-[13px] font-semibold text-brand-text transition-colors hover:bg-brand hover:text-brand-contrast [@media(pointer:fine)]:hidden"
                  >
                    <MdPhotoCamera size={16} aria-hidden="true" />
                    Take photo
                  </label>
                  <label
                    htmlFor="file-input"
                    className="inline-flex min-h-[40px] min-w-0 flex-1 cursor-pointer items-center gap-1.5 rounded-md border-[1px] border-ui-border bg-ui-surface px-3 py-1.5 text-[12px] font-medium text-ui-text-muted transition-colors hover:border-brand hover:text-brand lg:min-h-0 [@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:justify-center"
                  >
                    {t("orderDetails.attachFile")}
                  </label>
                </div>
                <div className="salte_attachment_list_main_wrap">
                  {picked.items.map((attachedFile, index) => (
                    <div
                      key={attachedFile.id}
                      className="sale_status_update_attachment_detail_box"
                    >
                      <TextField
                        variant="outlined"
                        className="attachment__field"
                        size="small"
                        value={attachedFile.name}
                        onChange={(e) =>
                          handleRenameFile(index, e.target.value)
                        }
                        InputProps={{
                          disableUnderline: true,
                        }}
                        style={{ border: "none", padding: "0" }}
                      />
                      <IconButton
                        onClick={() => handleRemoveFile(index)}
                        color="error"
                        className="icon_btn_remove_attachment"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
            <p
              id="child-modal-description"
              className="m-0 text-[12.5px] leading-[1.6] text-ui-text-muted"
            >
              <Trans
                t={t}
                i18nKey="orderDetails.changeStatusQuestion"
                values={{
                  from: data?.fullfilment_status?.name?.toUpperCase() ?? "-",
                  to: StatusName,
                }}
                components={{ b: <strong className="font-semibold text-ui-text" /> }}
              />
            </p>

            {(StatusName === "CANCELLED" ||
              data?.shipment?.shipment_status === "Cancelled" ||
              ((data?.payment_method_title === "Cash On Delivery" ||
                data?.payment_method_title === "Cash On Pickup") &&
                StatusName === "FULFILLED")) && (
              <div className="mt-2 rounded-md border border-state-danger-border bg-state-danger-bg px-3 py-2 text-[12px] font-medium text-state-danger-text md:text-[11.5px]">
                {StatusName === "CANCELLED" && (
                  <div>{t("orderDetails.noteCancelingIsAnIrreversibleAction")}</div>
                )}
                {data?.shipment?.shipment_status === "Cancelled" && (
                  <div>{t("orderDetails.noteShipmentForThisOrderIsCancelled")}</div>
                )}
                {(data?.payment_method_title === "Cash On Delivery" ||
                  data?.payment_method_title === "Cash On Pickup") &&
                  StatusName === "FULFILLED" && (
                    <div>{t("orderDetails.noteReceiveThePendingAmount")}</div>
                  )}
              </div>
            )}
          </div>

          {/* Phones: two big buttons side by side, clear of the home bar. */}
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-ui-border px-[12px] pt-[12px] pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] md:px-[24px] md:pb-[12px]">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loader}
              className="min-h-[44px] flex-1 rounded-md border-[1px] border-ui-border bg-ui-surface px-4 py-1.5 text-[14px] font-semibold text-ui-text-muted transition-colors hover:border-state-danger-border hover:text-state-danger-text disabled:cursor-not-allowed disabled:opacity-60 md:flex-[0_1_auto] md:text-[12px] lg:min-h-0 [@media(pointer:coarse)]:min-h-[44px]"
            >
              {t("orderDetails.decline")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loader}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-4 py-1.5 text-[14px] font-semibold text-brand-contrast transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-60 md:flex-[0_1_auto] md:justify-start md:text-[12px] lg:min-h-0 [@media(pointer:coarse)]:min-h-[44px]"
            >
              {loader && <CircularLoader size={12} />}
              {(loader && uploadLabel(upload)) || t("common:actions.confirm")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
